import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {Course, CourseElement, Lecturer, User} from '../../../shared/models';
import {Subject} from 'rxjs';
import {CourseService, SectionService} from '../../../shared/services';
import {ActivatedRoute, Router} from '@angular/router';
import {takeUntil} from 'rxjs/operators';
import {CourseUtil, SeoUtil, WindowUtil} from '../../../shared/utils';
import {ELEMENT_TYPES} from '../../../shared/enums';
import {AuthService} from '../../../shared/modules/auth/auth.service';
import {environment} from '../../../../environments/environment';
import {FilePipe} from '../../../shared/pipes/file/file.pipe';
import {DialogService} from '../../../shared/modules/dialog/dialog.service';
import {ExitSurveyDialogComponent} from '../../player/exit-survey-dialog/exit-survey-dialog.component';
import {NoAccessDialogComponent} from './no-access-dialog/no-access-dialog.component';
import { Faq } from '../../../shared/models';
import { FaqsService } from '../../../shared/services';
import {TranslateService} from '@ngx-translate/core';

declare var MainJs: any;

@Component({
  selector: 'app-course',
  templateUrl: './course.component.html',
  styleUrls: ['course.component.css']
})
export class CourseComponent implements OnInit, OnDestroy {
  user: User;

  course: Course;
  recommendedCourses: Array<Course> = [];
  downloadableFiles: Array<CourseElement> = [];
  faqs: Faq[];
  loadingCourses: boolean;
  enrolling: boolean;

  private destroy$ = new Subject();

  constructor(public courseUtil: CourseUtil,
              private route: ActivatedRoute,
              private router: Router,
              private cd: ChangeDetectorRef,
              private filePipe: FilePipe,
              private faqsService: FaqsService,
              private translate: TranslateService,
              private dialogService: DialogService,
              private sectionService: SectionService,
              private courseService: CourseService,
              private authService: AuthService,
              private windowUtil: WindowUtil,
              private seoUtil: SeoUtil) {
  }

  ngOnInit(): void {
    this.handleRouteChange();
    this.handleUserChange();
    this.fetchAllFaqs();
  }

  ngOnDestroy(): void {
    this.seoUtil.setDefaultMetaTags();
    this.seoUtil.setDefaultTitle();
    this.destroy$.next();
    this.destroy$.complete();
  }

  enrollCourse(): void {
    this.enrolling = true;
    this.courseService.enroll(this.course.id).subscribe(resp => {
      this.enrolling = false;
      if (resp.success) {
        this.router.navigate(['/player/' + this.course.id]).finally();
      }
    }, () => {
      this.router.navigate(['/not-found']).finally();
    });
  }

  courseRoute(): string {
    let route = '/player/' + this.course.id;
    if (this.course && this.course.enrolment && this.course.enrolment.last_section_id && this.course.enrolment.last_lesson_id) {
      route = '/player/' + this.course.id + '/' + this.course.enrolment.last_section_id + '/' +
        this.course.enrolment.last_lesson_id;
    }
    return route;
  }

  testDuration(): number {
    return this.courseUtil.courseDuration(this.course, [ELEMENT_TYPES.TEST_ELEMENT]);
  }

  lecturesDuration(): number {
    let totalDuration = this.courseUtil.courseDuration(this.course);
    let testDuration = this.testDuration();
    if (testDuration === totalDuration) {
      return 0;
    }
    return totalDuration - testDuration;
  }

  isWord(fileName: string): boolean {
    return fileName.match(/\.(docx|doc)$/) != null;
  }

  isPdf(fileName: string): boolean {
    return fileName.match(/\.(pdf)$/) != null;
  }

  lecturerName(lecturer: Lecturer): string {
    let name = '';
    if (lecturer.title) {
      name += lecturer.title + ' ';
    }
    name += lecturer.name;
    if (lecturer.institution) {
      name += ', ' + lecturer.institution;
    }
    return name;
  }

  lessonElementClick(elementId): void {
    if (this.course && this.course.enrolment && this.course.sections) {
      let section = this.course.sections.find(s => s.elements.find(el => el.id === elementId));
      if (section) {
        this.router.navigate(['/player/', this.course.id, section.id, elementId]).finally();
      } else {
        this.router.navigate([this.courseRoute()]).finally();
      }
    } else {
      this.dialogService.open(NoAccessDialogComponent, {
        data: {
          user: this.user,
          course: this.course
        }
      }).afterClosed.subscribe(() => {
      });
    }
  }

  private handleUserChange(): void {
    if (this.authService.isLoggedIn()) {
      this.authService.getAuthUser().subscribe(user => {
        this.user = user;
      });
    } else {
      this.user = null;
    }
    this.authService.userChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user;
      });
  }

  private fetchCourse(id: string): void {
    this.course = null;
    this.courseService.getCourse(id).subscribe(course => {
      this.course = course;
      this.cd.detectChanges();
      MainJs.tabs();
      MainJs.sliders();
      MainJs.accordion();
      MainJs.collapsible();
      MainJs.progress();
      this.fetchRecommendedCourses();
      this.fetchCourseDownloadableFiles();
      this.seoUtil.updateTitle(course.title, false);
      this.seoUtil.updateMetaTags([
        {property: 'og:title', content: course.title},
        {property: 'og:description', content: course.description},
        {
          property: 'og:image',
          content: this.filePipe.transform(course.mainImage || course.organizerImage, 'image', 'lms', 1280)
        },
        {property: 'og:url', content: environment.sites.root + 'courses/' + course.urlSlug}
      ]);
    }, (err) => {
      const route = err.status === 403 ? '/forbidden' : '/not-found';
      this.router.navigate([route], {skipLocationChange: true}).finally();
    });
  }

  private fetchRecommendedCourses(): void {
    this.loadingCourses = true;
    this.courseService.getCourses(1, 9, {
      categoryIds: [this.course.categoryId.toString()].join(',')
    }).subscribe(resp => {
      this.recommendedCourses = resp.data.filter(c => c.id !== this.course.id);
      this.loadingCourses = false;
    });
  }

  private fetchCourseDownloadableFiles(): void {
    this.sectionService.getSections(this.course.id).subscribe(sections => {
      this.downloadableFiles = sections.map(s => s.elements)
        .reduce((flat, next) => flat.concat(next), [])
        .filter(e => e.type === ELEMENT_TYPES.FILE_ELEMENT)
        .filter(e => e.content['fileData'] !== null &&
          (this.isWord(e.content['fileData']) || this.isPdf(e.content['fileData'])));
    });
  }

  private fetchAllFaqs(): void {
    this.faqsService.query({ params: { language: this.translate.currentLang } }).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (faqs: Faq[]) => {
        this.faqs = faqs;
      },
      (error) => {
        console.error('Error fetching FAQs:', error);
      }
    );
  }

  private handleRouteChange(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const id = params['id'];
        if (id) {
          this.fetchCourse(id);
        }
      });
  }

}
